import { useState } from "react";
import { useRoute, useLocation } from "wouter";
import * as Sentry from "@sentry/react";
import { useQuery as useConvexQuery, useMutation as useConvexMutation } from "convex/react";
import { toast } from "@/hooks/use-toast";
import Icon from "@/components/mnf/Icon";
import { GlassCard, PillTag, Btn, Avatar, SectionTitle } from "@/components/mnf/UI";
import AwardBadge from "@/components/mnf/AwardBadge";
import ShareRecipeSheet from "@/components/mnf/ShareRecipeSheet";
import SafetyAdvisoryCard from "@/components/mnf/SafetyAdvisoryCard";
import IngredientEditor from "@/components/mnf/IngredientEditor";
import AddToMealPlanSheet from "@/components/mnf/AddToMealPlanSheet";
import { queries, useStoreQuery, recipeSafetyForSeason, type Recipe } from "@/lib/store";
import { api } from "../../../convex/_generated/api";

/** One place to fail loudly: console, Sentry, and a word to her. */
function reportWriteFailure(what: string, err: unknown, title: string, description: string): void {
  console.error(`[RecipeDetail] ${what} failed`, err);
  Sentry.captureException(err, { tags: { subsystem: "recipe-detail" } });
  toast({ variant: "destructive", title, description });
}

/**
 * RecipeDetail
 *
 * Single-recipe deep-dive at /nourish/:recipeId. Preserves the original Nourish
 * page layout: hero card, family meal scaler, kids' plate toggle, Coach Kiley
 * note (when authored by Kiley), ingredient checklist, prep steps, then save +
 * Add to Garden CTAs. Now backed by store data so any recipe (Kiley, sister,
 * or imported) can render here.
 *
 * Loving a recipe is gated to "after eaten" upstream in Garden's meal plan flow.
 * The button on this page only appears if the sister already cooked it (we
 * mark this when they tap a meal plan slot as eaten that references this id).
 * For now we always show it but the copy reads "I cooked this · loved it" so
 * it stays honest.
 */

function fmtQty(n: number): string {
  if (Math.abs(n - Math.round(n)) < 0.05) return String(Math.round(n));
  const rounded = Math.round(n * 4) / 4;
  const whole = Math.floor(rounded);
  const frac = rounded - whole;
  const fracStr = frac === 0.25 ? "\u00bc" : frac === 0.5 ? "\u00bd" : frac === 0.75 ? "\u00be" : null;
  if (fracStr) return whole > 0 ? `${whole}${fracStr}` : fracStr;
  return n.toFixed(1).replace(/\.0$/, "");
}

export default function RecipeDetail() {
  const [, params] = useRoute<{ recipeId: string }>("/nourish/:recipeId");
  const [, setLocation] = useLocation();
  const recipeId = params?.recipeId ?? "";

  const me = useStoreQuery(queries.currentUser);

  // Recipe lookup: try Convex first (user-created recipes), fall back to
  // in-memory seed catalog (Today's Harvest, Trending, etc.).
  // Convex query: undefined while loading, null when not found, doc when found.
  const convexRecipe = useConvexQuery(api.recipesDb.recipeById, { id: recipeId });
  const seedRecipe = useStoreQuery(() => queries.recipeById(recipeId));
  // Loading: Convex hasn't responded yet AND seed lookup returned nothing.
  // Not-found: Convex explicitly returned null AND seed lookup returned nothing.
  const convexLoading = convexRecipe === undefined;
  const recipe: Recipe | null =
    (convexRecipe as Recipe | null | undefined) ?? seedRecipe ?? null;
  const isLoadingRecipe = !recipe && convexLoading;

  // Author byline: Convex-backed profile lookup with hardcoded Kiley
  // fallback inside profileByDomainId so seed recipes attribute correctly.
  const authorProfile = useConvexQuery(
    api.users.profileByDomainId,
    recipe ? { domainId: recipe.authorId } : "skip",
  );
  const author = authorProfile ?? { name: "", initials: "", isCoach: false };

  const convexIsSaved = useConvexQuery(
    api.recipesDb.isSavedBy,
    me && recipe ? { userId: me.id, recipeId: recipe.id } : "skip",
  );
  const isSaved = convexIsSaved ?? false;

  // Tier C: loves are persisted in Convex. hasLoved reflects the
  // recipeLoves table; the love mutation increments lovedCount + tier
  // on the recipe row in the same transaction.
  const hasLovedConvex = useConvexQuery(
    api.engagement.hasLovedRecipe,
    me && recipe ? { userId: me.id, recipeId: recipe.id } : "skip",
  );
  const hasLoved = hasLovedConvex ?? false;
  const loveRecipe = useConvexMutation(api.engagement.loveRecipe);

  // Cooks — separate signal so Coach Kiley sees what's actually being
  // made. Tapping "I cooked this" logs once; love button only unlocks
  // after a cook is logged.
  const hasCookedConvex = useConvexQuery(
    api.engagement.hasCookedRecipe,
    me && recipe ? { userId: me.id, recipeId: recipe.id } : "skip",
  );
  const hasCooked = hasCookedConvex ?? false;
  const logCook = useConvexMutation(api.engagement.logCook);

  const season = useStoreQuery(() => (me ? queries.seasonState(me.id) : null));
  const advisories = recipe && season ? recipeSafetyForSeason(recipe, season) : [];

  const saveRecipe   = useConvexMutation(api.recipesDb.saveRecipe);
  const unsaveRecipe = useConvexMutation(api.recipesDb.unsaveRecipe);

  // Comments — Convex-backed, oldest first. Skip until recipe resolves.
  const comments = useConvexQuery(
    api.engagement.commentsForRecipe,
    recipe ? { recipeId: recipe.id } : "skip",
  );
  const addRecipeComment = useConvexMutation(api.engagement.addRecipeComment);
  const deleteRecipeComment = useConvexMutation(api.engagement.deleteRecipeComment);
  const reportRecipeComment = useConvexMutation(api.engagement.reportComment);
  const [commentDraft, setCommentDraft] = useState("");
  const [commentError, setCommentError] = useState<string | null>(null);
  const [commentBusy, setCommentBusy] = useState(false);

  const [servings, setServings] = useState<number>(recipe?.servings ?? 4);
  const [kidsMode, setKidsMode] = useState<boolean>(false);
  const [shareOpen, setShareOpen] = useState<boolean>(false);
  const [planOpen, setPlanOpen] = useState<boolean>(false);
  const [editingIngredientIndex, setEditingIngredientIndex] = useState<number | null>(null);
  // Editable means this recipe lives in Convex (sister can change ingredients).
  // Seed catalog recipes (Today's Harvest, Trending) are read-only for now.
  const isEditable = convexRecipe != null;

  // Show a soft loading state while Convex is still resolving — avoids
  // flashing the "no longer in the garden" empty state on every navigation.
  if (isLoadingRecipe || !me) {
    return (
      <div
        className="px-5 pt-10 text-center mnf-body"
        style={{ color: "var(--mnf-tg)" }}
        data-testid="recipe-detail-loading"
      >
        <p>Reading recipe…</p>
      </div>
    );
  }
  if (!recipe) {
    return (
      <div className="px-5 pt-10 text-center mnf-body" style={{ color: "var(--mnf-tg)" }}>
        <p>That recipe is no longer in the garden.</p>
        <button
          data-testid="button-back-to-nourish"
          onClick={() => setLocation("/nourish")}
          className="mt-3 mnf-body underline"
          style={{ color: "var(--mnf-f)" }}
        >
          Back to Nourish
        </button>
      </div>
    );
  }

  const baseServings = recipe.servings;
  const scale = (servings / baseServings) * (kidsMode ? 0.6 : 1);
  const scaledIngredients = recipe.ingredients.map((b) => ({
    ...b,
    qtyDisplay: `${fmtQty(b.qty * (servings / baseServings))} ${b.unit} ${b.item}`.trim(),
  }));
  const scaledKcal    = Math.round(recipe.kcal    * (kidsMode ? 0.6 : 1));
  const scaledProtein = Math.round(recipe.proteinG * (kidsMode ? 0.6 : 1));
  const scaledFiber   = Math.round(recipe.fiberG  * (kidsMode ? 0.6 : 1));

  const onToggleSave = () => {
    if (isSaved) {
      void unsaveRecipe({ userId: me.id, recipeId: recipe.id }).catch((err: unknown) => {
        reportWriteFailure(
          "unsaving a recipe", err,
          "We couldn't unsave this",
          "It's still in your saved recipes. Check your connection and try again.",
        );
      });
    } else {
      void saveRecipe({ userId: me.id, recipeId: recipe.id }).catch((err: unknown) => {
        reportWriteFailure(
          "saving a recipe", err,
          "We couldn't save this recipe",
          "It didn't make it to your saved recipes. Check your connection and tap Save again.",
        );
      });
    }
  };
  const onCookedClick = () => {
    if (!recipe || !me) return;
    // Two-stage: log cook if not yet, else upgrade to love.
    if (!hasCooked) {
      void logCook({ userId: me.id, recipeId: recipe.id }).catch((err: unknown) => {
        reportWriteFailure(
          "logging a cook", err,
          "We couldn't log that cook",
          "This recipe isn't marked as cooked yet. Check your connection and tap it again.",
        );
      });
    } else if (!hasLoved) {
      void loveRecipe({ userId: me.id, recipeId: recipe.id }).catch((err: unknown) => {
        reportWriteFailure(
          "loving a recipe", err,
          "We couldn't add your love",
          "Your love didn't save on this recipe. Check your connection and tap it again.",
        );
      });
    }
  };

  const onSubmitComment = async () => {
    if (!recipe || commentBusy) return;
    const body = commentDraft.trim();
    if (!body) return;
    if (body.length > 480) {
      setCommentError("Keep comments under 480 characters.");
      return;
    }
    setCommentBusy(true);
    setCommentError(null);
    try {
      await addRecipeComment({ userId: me.id, recipeId: recipe.id, body });
      setCommentDraft("");
    } catch (err) {
      // Draft stays in the box — nothing was posted.
      setCommentError("That didn't post. Your words are still here — try again.");
      reportWriteFailure(
        "posting a comment", err,
        "Your comment didn't post",
        "It wasn't added to this recipe. Your words are still in the box — check your connection and post again.",
      );
    } finally {
      setCommentBusy(false);
    }
  };

  return (
    <>
      {/* Top bar */}
      <div className="px-5 pt-2 pb-1 flex items-center justify-between" style={{ color: "var(--mnf-f)" }}>
        <button
          data-testid="button-back"
          onClick={() => setLocation("/nourish")}
          className="flex items-center gap-2 hover-elevate rounded-full px-2 py-1"
          aria-label="Back to Nourish"
        >
          <Icon name="arrow_back" size={20} />
          <span className="mnf-serif italic" style={{ fontSize: 15 }}>Back to Nourish</span>
        </button>
        <button
          data-testid="button-share-recipe"
          onClick={() => setShareOpen(true)}
          className="hover-elevate rounded-full p-2"
          aria-label="Share recipe"
          style={{ color: "var(--mnf-f)" }}
        >
          <Icon name="ios_share" size={22} />
        </button>
      </div>

      <div className="px-5 pt-3 space-y-5">
        <div>
          <p className="mnf-eyebrow">{recipe.source === "kiley" ? "Coach pick" : "From the community"}</p>
          <h1
            className="mnf-serif leading-[1.05] mt-1"
            style={{ fontSize: 34, fontWeight: 500, color: "var(--mnf-fd)" }}
          >
            {recipe.title}
          </h1>
        </div>

        {/* Hero recipe card */}
        <div
          className="relative overflow-hidden mnf-fade-up"
          style={{
            borderRadius: 24,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.65) 100%), " +
              recipe.heroGradient,
            border: "1px solid rgba(30,74,53,0.10)",
            boxShadow: "0 14px 36px rgba(30,74,53,0.18)",
          }}
        >
          {recipe.awardTier !== "none" && (
            <div style={{ position: "absolute", top: 14, right: 14, zIndex: 2 }}>
              <AwardBadge tier={recipe.awardTier} count={recipe.lovedCount} size="sm" />
            </div>
          )}
          <div className="relative flex items-end justify-center" style={{ height: 180, paddingBottom: 18 }}>
            <div
              style={{
                width: 200, height: 90,
                background: "radial-gradient(ellipse at 50% 40%, #C0784A 5%, #8a4f2a 55%, #4a2914 100%)",
                borderRadius: "50% 50% 46% 46% / 50% 50% 90% 90%",
                position: "relative",
                boxShadow: "0 12px 22px rgba(0,0,0,0.40), inset 0 -10px 14px rgba(0,0,0,0.32)",
              }}
            >
              {recipe.heroEmojis.slice(0, 4).map((emo, i) => {
                const positions = [
                  { top: -52, left: 22, fontSize: 56 },
                  { top: -58, left: 78, fontSize: 52 },
                  { top: -44, right: 18, fontSize: 46 },
                  { top: -30, left: 145, fontSize: 30 },
                ];
                return (
                  <div key={i} style={{ position: "absolute", ...positions[i] }} aria-hidden>
                    {emo}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="px-5 pt-1 pb-5">
            <div className="flex items-center gap-2 flex-wrap">
              <PillTag tone="coral">
                <Icon name="schedule" size={11} /> {recipe.durationMin} min
              </PillTag>
              <PillTag tone="forest">
                <Icon name="diversity_1" size={11} color="#F5F0E8" /> Serves {servings}
              </PillTag>
              {kidsMode && <PillTag tone="gold">Kids' plate</PillTag>}
            </div>
            <p
              className="text-[11px] mnf-body mt-2"
              style={{
                color: "rgba(255,255,255,0.85)",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              {scaledKcal} kcal · {scaledProtein}g protein · {scaledFiber}g fiber per serving
            </p>
          </div>
        </div>

        {/* Description and author */}
        <GlassCard padding={16}>
          <div className="flex items-center gap-2.5">
            <Avatar initials={author.initials} size={36} ringColor={author.isCoach ? "rgba(192,120,74,0.5)" : undefined} />
            <div className="flex-1 min-w-0">
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)" }}>
                {author.isCoach ? "Coach Kiley" : `Shared by ${author.name}`}
              </p>
              <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.5, marginTop: 4 }}>
                {recipe.description}
              </p>
            </div>
          </div>
        </GlassCard>

        {/* Family meal scaler */}
        <GlassCard padding={16}>
          <div className="flex items-baseline justify-between mb-3">
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Cook for the whole table</p>
            <span className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>Quantities scale automatically</span>
          </div>
          <div className="flex items-center gap-2">
            {[2, 4, 6, 8].map((n) => (
              <button
                key={n}
                data-testid={`button-servings-${n}`}
                onClick={() => setServings(n)}
                className="flex-1 mnf-body transition-all hover-elevate"
                style={{
                  padding: "10px 0", borderRadius: 14,
                  background: servings === n ? "var(--mnf-f)" : "rgba(30,74,53,0.06)",
                  color: servings === n ? "#F5F0E8" : "var(--mnf-fd)",
                  fontSize: 13, fontWeight: 700,
                  border: "1px solid transparent",
                  cursor: "pointer",
                }}
              >
                {n}
                <span className="block mnf-body" style={{ fontSize: 9, fontWeight: 500, opacity: 0.75, letterSpacing: "0.06em", textTransform: "uppercase", marginTop: 2 }}>servings</span>
              </button>
            ))}
          </div>
          <button
            data-testid="button-kids-plate"
            onClick={() => setKidsMode(!kidsMode)}
            className="flex items-center gap-3 w-full mt-3 transition-all hover-elevate"
            style={{
              padding: "12px 14px", borderRadius: 16,
              background: kidsMode ? "rgba(212,160,78,0.14)" : "rgba(30,74,53,0.04)",
              border: `1px solid ${kidsMode ? "var(--mnf-gold)" : "rgba(30,74,53,0.06)"}`,
              cursor: "pointer",
            }}
          >
            <span style={{ fontSize: 22 }} aria-hidden>{"\u2728"}</span>
            <div className="flex-1 text-left">
              <p className="mnf-body" style={{ fontSize: 13, fontWeight: 700, color: "var(--mnf-fd)" }}>Add a kids' plate</p>
              <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)", lineHeight: 1.4 }}>
                Smaller portion, dressing on the side, garnish optional.
              </p>
            </div>
            <span
              style={{
                width: 36, height: 22, borderRadius: 999,
                background: kidsMode ? "var(--mnf-gold)" : "rgba(30,74,53,0.18)",
                position: "relative", transition: "background 200ms",
                flexShrink: 0,
              }}
            >
              <span style={{
                position: "absolute", top: 2,
                left: kidsMode ? 16 : 2,
                width: 18, height: 18, borderRadius: 999, background: "#fff",
                transition: "left 200ms cubic-bezier(0.34, 1.56, 0.64, 1)",
              }} />
            </span>
          </button>
        </GlassCard>

        {/* Coach Kiley note (only for Kiley-authored recipes that have scripture pairing) */}
        {author.isCoach && recipe.scripturePairing && (
          <GlassCard
            padding={18}
            className="mnf-fade-up"
            style={{
              background: "linear-gradient(165deg, #1E4A35 0%, #163828 100%)",
              color: "#F5F0E8",
              border: "1px solid rgba(245,240,232,0.10)",
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Avatar initials="K" size={32} />
              <p className="mnf-eyebrow" style={{ color: "rgba(229,185,151,0.95)" }}>
                Coach Kiley's note
              </p>
            </div>
            <p className="mnf-serif italic" style={{ fontSize: 16, lineHeight: 1.5, color: "#F5F0E8" }}>
              "{recipe.scripturePairing}"
            </p>
          </GlassCard>
        )}

        {/* Season-aware safety advisory (renders nothing if no advisories) */}
        <SafetyAdvisoryCard advisories={advisories} season={season} />

        {/* Ingredients */}
        <SectionTitle
          eyebrow="Ingredients"
          title="From the pantry"
          trailing={`${scaledIngredients.length} items · ${servings} servings`}
        />
        <GlassCard padding={0} className="mnf-fade-up">
          {scaledIngredients.map((ing, i) => {
            const rowStyle = {
              borderBottom: i < scaledIngredients.length - 1 ? "1px solid rgba(30,74,53,0.06)" : "none",
            } as const;
            const inner = (
              <>
                <span
                  className="flex items-center justify-center shrink-0"
                  style={{
                    width: 22, height: 22, borderRadius: "50%",
                    background: "rgba(30,74,53,0.06)",
                    border: "1.5px solid rgba(30,74,53,0.18)",
                  }}
                />
                <p
                  className="mnf-body flex-1"
                  style={{ fontSize: 13.5, fontWeight: 500, color: "var(--mnf-tx)", lineHeight: 1.4 }}
                >
                  {ing.qtyDisplay}
                </p>
                {isEditable && (
                  <Icon name="edit" size={14} color="var(--mnf-tg)" />
                )}
              </>
            );
            return isEditable ? (
              <button
                key={i}
                data-testid={`button-edit-ingredient-${i}`}
                onClick={() => setEditingIngredientIndex(i)}
                className="flex items-center gap-3 px-4 py-3 w-full text-left hover-elevate"
                style={{ ...rowStyle, background: "transparent", border: "none", cursor: "pointer" }}
              >
                {inner}
              </button>
            ) : (
              <div key={i} className="flex items-center gap-3 px-4 py-3" style={rowStyle}>
                {inner}
              </div>
            );
          })}
        </GlassCard>
        {isEditable && editingIngredientIndex !== null && (
          <IngredientEditor
            open
            onClose={() => setEditingIngredientIndex(null)}
            recipeId={recipe.id}
            index={editingIngredientIndex}
            current={recipe.ingredients[editingIngredientIndex]}
          />
        )}

        {/* Prep steps */}
        <SectionTitle eyebrow="Prep steps" title="Step by step" />
        <div className="space-y-3 mnf-fade-up">
          {recipe.steps.map((s, i) => (
            <GlassCard key={i} padding={16}>
              <div className="flex items-start gap-3">
                <div
                  className="flex items-center justify-center shrink-0 mnf-serif"
                  style={{
                    width: 36, height: 36, borderRadius: "50%",
                    background: i === 0 ? "rgba(192,120,74,0.18)" : "rgba(30,74,53,0.06)",
                    color: i === 0 ? "var(--mnf-t)" : "var(--mnf-f)",
                    fontSize: 13, fontWeight: 600,
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="flex-1">
                  <p className="mnf-serif" style={{ fontSize: 15, fontWeight: 500, color: "var(--mnf-fd)" }}>
                    {s.title}
                  </p>
                  <p className="mnf-body mt-1" style={{ fontSize: 13, lineHeight: 1.55, color: "var(--mnf-tg)" }}>
                    {s.body}
                  </p>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>

        {/* Save and Add to Garden */}
        <div className="grid grid-cols-2 gap-2.5">
          <Btn
            data-testid="button-save-recipe"
            variant={isSaved ? "ghost" : "secondary"}
            block
            size="lg"
            onClick={onToggleSave}
          >
            <Icon name={isSaved ? "bookmark" : "bookmark_add"} size={18} filled={isSaved} />
            {isSaved ? "Saved" : "Save recipe"}
          </Btn>
          <Btn
            data-testid="button-add-to-garden"
            variant="primary"
            block
            size="lg"
            onClick={() => setPlanOpen(true)}
          >
            <Icon name="event_available" size={18} /> Add to plan
          </Btn>
        </div>

        {/* Cook + love (two-stage for honesty: log cook first, then love) */}
        <button
          data-testid="button-cooked-loved"
          onClick={onCookedClick}
          disabled={hasLoved}
          className="w-full mnf-body hover-elevate transition-all"
          style={{
            padding: "14px 16px",
            borderRadius: 16,
            background: hasLoved ? "var(--mnf-fiberBg)" : "rgba(192,120,74,0.08)",
            border: `1px solid ${hasLoved ? "var(--mnf-fiberBorder)" : "rgba(192,120,74,0.24)"}`,
            color: hasLoved ? "#2C6248" : "var(--mnf-t)",
            fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
            cursor: hasLoved ? "default" : "pointer",
            opacity: hasLoved ? 0.85 : 1,
          }}
        >
          {hasLoved ? (
            <span className="inline-flex items-center gap-2 justify-center">
              <Icon name="favorite" size={16} filled /> You nourished a sister
            </span>
          ) : hasCooked ? (
            <span className="inline-flex items-center gap-2 justify-center">
              <Icon name="favorite_border" size={16} /> Tap to love it
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 justify-center">
              <Icon name="restaurant" size={16} /> I cooked this
            </span>
          )}
        </button>

        {/* Comments section (Tier C) */}
        <div className="space-y-2">
          <SectionTitle
            eyebrow="Sisterhood"
            title="Comments"
            trailing={comments ? `${comments.length}` : undefined}
          />
          {comments === undefined ? (
            <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>
              Loading…
            </p>
          ) : comments.length === 0 ? (
            <p className="mnf-body italic" style={{ fontSize: 13, color: "var(--mnf-tg)" }}>
              Be the first to encourage a sister.
            </p>
          ) : (
            <ul className="space-y-2.5">
              {comments.map((c) => (
                <CommentRow
                  key={c._id}
                  commentId={c._id}
                  authorId={c.authorId}
                  body={c.body}
                  createdAt={c.createdAt}
                  canDelete={c.authorId === me.id}
                  onDelete={() => {
                    void deleteRecipeComment({ commentId: c._id }).catch((err: unknown) => {
                      reportWriteFailure(
                        "deleting a comment", err,
                        "We couldn't delete that comment",
                        "It's still on this recipe. Check your connection and try again.",
                      );
                    });
                  }}
                  onReport={
                    c.authorId === me.id
                      ? undefined
                      : () => {
                          void reportRecipeComment({ userId: me.id, commentId: c._id }).catch((err: unknown) => {
                            reportWriteFailure(
                              "reporting a comment", err,
                              "We couldn't send that report",
                              "Nothing was reported. Check your connection and try again.",
                            );
                          });
                        }
                  }
                />
              ))}
            </ul>
          )}

          <div className="space-y-1.5 pt-1">
            <textarea
              value={commentDraft}
              onChange={(e) => {
                setCommentDraft(e.target.value);
                if (commentError) setCommentError(null);
              }}
              placeholder="Encourage a sister…"
              aria-label="Comment"
              rows={2}
              maxLength={480}
              className="mnf-body w-full"
              style={{
                padding: "10px 12px",
                borderRadius: 12,
                background: "rgba(255,255,255,0.7)",
                border: "1px solid rgba(30,74,53,0.16)",
                fontSize: 13,
                color: "var(--mnf-tx)",
                resize: "vertical",
              }}
            />
            <div className="flex items-center justify-between gap-2">
              <span className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>
                {commentError ?? `${commentDraft.length}/480`}
              </span>
              <Btn
                variant="primary"
                size="sm"
                onClick={onSubmitComment}
                disabled={commentBusy || !commentDraft.trim()}
                data-testid="button-submit-comment"
              >
                <Icon name={commentBusy ? "hourglass_empty" : "send"} size={14} />
                {commentBusy ? "Sending…" : "Post"}
              </Btn>
            </div>
          </div>
        </div>

        <div className="h-2" />
      </div>

      <ShareRecipeSheet
        recipe={recipe}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
      />

      <AddToMealPlanSheet
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        userId={me.id}
        recipeId={recipe.id}
        recipeServings={recipe.servings}
        recipeTitle={recipe.title}
      />
    </>
  );
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const min = 60_000;
  const hr = 60 * min;
  const day = 24 * hr;
  if (diff < min) return "just now";
  if (diff < hr) return `${Math.floor(diff / min)}m`;
  if (diff < day) return `${Math.floor(diff / hr)}h`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d`;
  return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function CommentRow({
  commentId: _commentId,
  authorId,
  body,
  createdAt,
  canDelete,
  onDelete,
  onReport,
}: {
  commentId: string;
  authorId: string;
  body: string;
  createdAt: number;
  canDelete: boolean;
  onDelete: () => void;
  onReport?: () => void;
}) {
  const [reported, setReported] = useState(false);
  const profile = useConvexQuery(api.users.profileByDomainId, { domainId: authorId });
  const name = profile?.name ?? "Sister";
  const initials = profile?.initials ?? "S";
  const isCoach = profile?.isCoach ?? false;
  return (
    <li className="flex gap-2.5">
      <Avatar
        initials={initials}
        size={32}
        ringColor={isCoach ? "rgba(160,120,32,0.55)" : undefined}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="mnf-serif" style={{ fontSize: 13, fontWeight: 500, color: "var(--mnf-fd)" }}>
            {name}
          </span>
          <span className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>
            {relativeTime(createdAt)}
          </span>
          {canDelete ? (
            <button
              onClick={onDelete}
              aria-label="Delete comment"
              className="ml-auto"
              style={{ fontSize: 11, color: "var(--mnf-tg)", opacity: 0.7 }}
            >
              Delete
            </button>
          ) : onReport && !reported ? (
            <button
              onClick={() => { setReported(true); onReport(); }}
              aria-label="Report comment"
              className="ml-auto"
              style={{ fontSize: 11, color: "var(--mnf-tg)", opacity: 0.6 }}
            >
              Report
            </button>
          ) : reported ? (
            <span className="ml-auto" style={{ fontSize: 11, color: "var(--mnf-tg)", opacity: 0.6 }}>
              Reported
            </span>
          ) : null}
        </div>
        <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tx)", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
          {body}
        </p>
      </div>
    </li>
  );
}
